import { DOCUMENT } from '@angular/common';
import { inject, signal } from '@angular/core';

import type {
  IPortalAvailability,
  PortalAvailabilityStatus,
  RealtimeLinkState,
} from './i-portal-availability';
import { TECHNICAL_LOG_HANDLER } from '../telemetry';

export class PortalAvailability implements IPortalAvailability {
  private readonly document = inject(DOCUMENT);
  private readonly technicalLog = inject(TECHNICAL_LOG_HANDLER);

  /**
   * Un fallimento bloccante non si toglie da solo: la pagina che l'ha subito e' rimasta a meta' — lo
   * storico vuoto, la visita non avviata — e mostrarla di nuovo sarebbe mostrare una pagina rotta. Si
   * riparte ricaricando.
   */
  private readonly _status = signal<PortalAvailabilityStatus>('available');
  readonly status = this._status.asReadonly();

  private readonly _realtime = signal<RealtimeLinkState>({ phase: 'connected' });
  readonly realtime = this._realtime.asReadonly();

  reportOutage(): void {
    if (this._status() === 'unavailable') return;

    this.technicalLog.record({ kind: 'error', message: 'Portal unavailable: blocking request failed' });
    this._status.set('unavailable');
  }

  reportOperatorNotEnabled(): void {
    if (this._status() !== 'available') return;

    this.technicalLog.record({ kind: 'error', message: 'Operator not enabled on this domain' });
    this._status.set('operator-not-enabled');
  }

  reportRealtime(state: RealtimeLinkState): void {
    const previous = this._realtime().phase;
    // Il diario vede i cambi di fase, non ogni secondo del conto alla rovescia.
    if (state.phase !== previous) {
      this.technicalLog.record({
        kind: state.phase === 'connected' ? 'network' : 'error',
        message: `Realtime link ${state.phase}`,
      });
    }
    this._realtime.set(state);
  }

  retry(): void {
    this.document.defaultView?.location.reload();
  }
}
